/*
  Gaia Iluka — Culinary Partners: cinematic scroll engine
  File: assets/gi-cp-cinematic.js
  Loaded only by the Culinary Partners template (layout/theme.liquid,
  template.suffix == 'wholesale'). Nothing else on the site uses it.

  WHY THIS EXISTS
  Every chapter on this page previously used the same sitewide .gi-reveal
  fade-up. Seven identical arrivals is the Brand Brain's own named
  anti-pattern ("blanket fade-up-on-scroll", VOICE & VISUAL 26) and it is
  why the page reads as a document rather than a film. This gives each
  chapter its own motion identity and, more importantly, makes motion a
  function of scroll POSITION rather than a one-shot trigger — that is
  what produces pacing.

  WHAT IT PROVIDES

  1. --gi-cp-p  — per-section scroll progress, 0 as the section's top
     reaches the bottom of the viewport, 1 as its bottom leaves the top.
     Written to any [data-cp-chapter] element. CSS reads it directly, so
     the visual behaviour lives in gi-cp-cinematic.css, not here.

  2. --gi-cp-in — a 0..1 entry ramp over the first 45% of that travel, for
     things that should resolve and then hold rather than track forever.

  3. .is-lit — set on the chapter nearest the viewport centre. Drives the
     chapter rail and the header inversion.

  4. Staggered element entry — [data-cp-rise] children resolve in sequence
     with a direction taken from the chapter, replacing the uniform fade.

  Header inversion is NOT handled here. assets/gi-header-theme.js is loaded
  sitewide and already observes [data-header-theme], so each chapter simply
  declares that attribute alongside data-cp-ground and the existing script
  does the work. The chapters previously carried no such attribute, which
  is why the header stayed light over the dark ones.

  MOTION BUDGET
  Brand Brain VOICE & VISUAL 23: "most of the experience quiet, a few
  moments cinematic, never constant animation." Scroll-linked transforms
  are limited to the opening veil, the food beats' counter-parallax and
  the product hold. Reading sections get entry motion only, then stop.

  prefers-reduced-motion: all scroll-linked work is skipped, every element
  is set to its resolved state immediately, and no rAF loop is started.
*/

(function () {
  'use strict';

  var reduce = window.matchMedia('(prefers-reduced-motion: reduce)');
  var chapters = [].slice.call(document.querySelectorAll('[data-cp-chapter]'));
  if (!chapters.length) return;

  /* Declared before the reduced-motion branch below, which calls buildRail()
     and would otherwise hit a hoisted-but-undefined railMarks. */
  var railMarks = [];

  /* ---- Reduced motion: resolve everything, wire nothing. ------------- */
  function resolveAll() {
    chapters.forEach(function (el) {
      el.style.setProperty('--gi-cp-p', '0.5');
      el.style.setProperty('--gi-cp-in', '1');
      el.classList.add('is-resolved');
    });
    [].forEach.call(document.querySelectorAll('[data-cp-rise]'), function (el) {
      el.classList.add('is-risen');
    });
  }

  if (reduce.matches) {
    resolveAll();
    buildRail(true);
    return;
  }

  /* ---- Entry stagger -------------------------------------------------
     One observer for every [data-cp-rise]. Delay comes from the element's
     index within its own chapter, so a chapter with six beats staggers
     across six steps regardless of what came before it on the page. */
  var STEP = 90; /* ms between siblings — slow enough to read as cadence */

  var riseObserver = new IntersectionObserver(function (entries) {
    entries.forEach(function (entry) {
      if (!entry.isIntersecting) return;
      var el = entry.target;
      var delay = parseInt(el.getAttribute('data-cp-rise'), 10);
      if (isNaN(delay)) delay = 0;
      setTimeout(function () { el.classList.add('is-risen'); }, delay * STEP);
      riseObserver.unobserve(el);
    });
  }, { rootMargin: '0px 0px -12% 0px', threshold: 0.15 });

  [].forEach.call(document.querySelectorAll('[data-cp-rise]'), function (el) {
    riseObserver.observe(el);
  });

  /* ---- Which chapters are worth measuring ----------------------------
     Only chapters currently on or near screen get progress written every
     frame. Off-screen ones are skipped entirely. */
  var active = [];

  var liveObserver = new IntersectionObserver(function (entries) {
    entries.forEach(function (entry) {
      var i = active.indexOf(entry.target);
      if (entry.isIntersecting && i === -1) active.push(entry.target);
      else if (!entry.isIntersecting && i !== -1) active.splice(i, 1);
    });
    if (active.length) start();
  }, { rootMargin: '25% 0px 25% 0px' });

  chapters.forEach(function (el) { liveObserver.observe(el); });

  /* ---- Chapter rail --------------------------------------------------
     A column of hairlines at the right edge, one per chapter, the current
     one drawn longer. No numbers, no dots, no pill. It reads as a margin
     mark in a book, which is the register this page is written in. */
  function buildRail(staticMode) {
    if (chapters.length < 3) return;
    var rail = document.createElement('nav');
    rail.className = 'gi-cp-rail';
    rail.setAttribute('aria-label', 'Chapters');

    chapters.forEach(function (ch, i) {
      var name = ch.getAttribute('data-cp-name') || '';
      var num = (i < 10 ? '0' : '') + i;
      var id = ch.getAttribute('id');
      var mark = document.createElement(id ? 'a' : 'span');
      mark.className = 'gi-cp-rail__mark';
      if (id) mark.href = '#' + id;
      mark.innerHTML =
        '<span class="gi-cp-rail__num">' + num + '</span>' +
        '<span class="gi-cp-rail__line"></span>' +
        '<span class="gi-cp-rail__name">' + name + '</span>';
      if (!name) mark.querySelector('.gi-cp-rail__name').remove();
      rail.appendChild(mark);
      railMarks.push(mark);
    });

    document.body.appendChild(rail);
    if (staticMode) rail.classList.add('is-visible');
  }

  buildRail(false);

  var rail = document.querySelector('.gi-cp-rail');

  /* Visible immediately, including inside the Opening chapter itself —
     it is the numbered index the brief asked to sit inside the hero,
     not a scroll-progress widget that earns its place after. Previously
     gated behind scrollY > vh * 0.75; that gate is removed, not added
     to, in the frame() loop below. */
  if (rail) rail.classList.add('is-visible');

  /* ---- The loop ------------------------------------------------------ */
  var running = false;
  var vh = window.innerHeight;
  var lastLit = null;

  function clamp01(n) { return n < 0 ? 0 : n > 1 ? 1 : n; }

  function frame() {
    if (!active.length) { running = false; return; }

    var centre = vh / 2;
    var best = null;
    var bestDist = Infinity;

    for (var i = 0; i < active.length; i++) {
      var el = active[i];
      var r = el.getBoundingClientRect();

      /* 0 when the top edge sits at the viewport bottom,
         1 when the bottom edge has passed the viewport top. */
      var travel = r.height + vh;
      var p = clamp01((vh - r.top) / travel);
      el.style.setProperty('--gi-cp-p', p.toFixed(4));

      /* Entry ramp: resolves over the first 45% of travel, then holds. */
      el.style.setProperty('--gi-cp-in', clamp01(p / 0.45).toFixed(4));

      var dist = Math.abs(r.top + r.height / 2 - centre);
      if (dist < bestDist) { bestDist = dist; best = el; }
    }

    /* Chapter nearest the viewport centre owns the rail and the header. */
    if (best && best !== lastLit) {
      if (lastLit) lastLit.classList.remove('is-lit');
      best.classList.add('is-lit');

      var idx = chapters.indexOf(best);
      railMarks.forEach(function (m, i) {
        m.classList.toggle('is-current', i === idx);
        m.classList.toggle('is-passed', i < idx);
      });

      /* The rail's own ink follows the chapter ground so the marks stay
         legible; the HEADER is left to assets/gi-header-theme.js. */
      if (rail) {
        rail.style.setProperty(
          '--gi-cp-ink',
          best.getAttribute('data-cp-ground') === 'dark'
            ? 'var(--primary-200)'
            : 'var(--text-primary)'
        );
      }

      lastLit = best;
    }

    requestAnimationFrame(frame);
  }

  function start() {
    if (running) return;
    running = true;
    requestAnimationFrame(frame);
  }

  window.addEventListener('resize', function () { vh = window.innerHeight; }, { passive: true });

  /* If the user flips the OS setting mid-session, stop animating. */
  var onReduceChange = function () { if (reduce.matches) { active.length = 0; resolveAll(); } };
  if (reduce.addEventListener) reduce.addEventListener('change', onReduceChange);
  else if (reduce.addListener) reduce.addListener(onReduceChange);

  start();
})();
